import { useState, type FormEvent } from 'react';
import {
  useCounterparties,
  useCreateCounterparty,
  useDeleteCounterparty,
  useUpdateCounterparty,
} from '../hooks/useCounterparties';
import type { Counterparty } from '../types';

export default function CounterpartiesPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, error } = useCounterparties(includeInactive);
  const createMut = useCreateCounterparty();
  const updateMut = useUpdateCounterparty();
  const deleteMut = useDeleteCounterparty();

  const [editing, setEditing] = useState<Counterparty | null>(null);
  const [showForm, setShowForm] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const invNum = String(fd.get('invoiceRegistrationNumber')).trim();
    const input = {
      name: String(fd.get('name')),
      invoiceRegistrationNumber: invNum.length > 0 ? invNum : null,
    };

    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...input });
      } else {
        await createMut.mutateAsync(input);
      }
      setEditing(null);
      setShowForm(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '保存失敗');
    }
  };

  const onDelete = async (cp: Counterparty) => {
    if (!confirm(`「${cp.name}」を非表示にしますか？`)) return;
    await deleteMut.mutateAsync(cp.id);
  };

  return (
    <div>
      <div className="page-header">
        <h2>取引先マスタ</h2>
        <div className="toolbar">
          <label>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />{' '}
            非表示も含む
          </label>
          <button
            className="primary"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            + 新規追加
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{(error as Error).message}</div>}

      {showForm && (
        <div className="card">
          <h3>{editing ? '編集' : '新規追加'}</h3>
          <form onSubmit={onSubmit}>
            <div className="form-row">
              <label>取引先名 *</label>
              <input name="name" defaultValue={editing?.name ?? ''} required maxLength={200} />
            </div>
            <div className="form-row">
              <label>インボイス登録番号</label>
              <input
                name="invoiceRegistrationNumber"
                defaultValue={editing?.invoiceRegistrationNumber ?? ''}
                pattern="^T\d{13}$"
                placeholder="T1234567890123（T + 13桁数字）"
                maxLength={14}
              />
            </div>
            <div className="toolbar">
              <button type="submit" className="primary" disabled={createMut.isPending || updateMut.isPending}>
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading && <div>読み込み中...</div>}

      {data && (
        <table>
          <thead>
            <tr>
              <th>取引先名</th>
              <th style={{ width: 200 }}>インボイス登録番号</th>
              <th style={{ width: 100 }}>状態</th>
              <th style={{ width: 160 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {data.map((cp) => (
              <tr key={cp.id}>
                <td>{cp.name}</td>
                <td>
                  {cp.invoiceRegistrationNumber ?? <span className="text-muted">未登録</span>}
                </td>
                <td>
                  {cp.isActive ? (
                    <span className="badge active">有効</span>
                  ) : (
                    <span className="badge voided">非表示</span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => {
                      setEditing(cp);
                      setShowForm(true);
                    }}
                  >
                    編集
                  </button>{' '}
                  {cp.isActive === 1 && (
                    <button onClick={() => onDelete(cp)} className="danger">
                      非表示
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
